import React, { useEffect } from 'react';

import { observer } from 'mobx-react';
import { FormProvider, useForm } from 'react-hook-form';

import classes from './SelectCoachStep.module.scss';
import { PageTitle } from '../../../../../shared/components/ui-components';
import { ButtonRow } from '../../../../components/customer-self-setup-components';
import { UseStepperReturn } from '../../../../../shared/hooks/useStepper';
import { coachStore } from '../../../../stores/customerSelfSetupStores/coachStore';
import { CoachListFilterFormValues } from '../../../../interfaces/coachList/CoachListFilters';
import { CoachTable } from './CoachTable/CoachTable';
import { useAutoSubmitForm } from '../../../../../shared/hooks/useAutoSubmitForm';
import { useScrollToTop } from '../../../../../shared/hooks/useScrollToTop';
import { useUpsertCustomer } from '../../../../hooks/customerSelfSetupHooks/useUpsertCustomer';
import { CONSULTATION_TYPE } from '../../../../../shared/constants/coach/consultationType';
import { entitySignUpStore } from '../../../../../shared/stores/entity/entitySignUpStore';

type SelectCoachStepProps = {
  stepperMethods: UseStepperReturn;
};

const SelectCoachStep = observer(({ stepperMethods }: SelectCoachStepProps) => {
  useScrollToTop();

  const { selectedCoach, filterMethods } = coachStore;
  const { entityId } = entitySignUpStore;

  const formMethods = useForm<CoachListFilterFormValues>({
    defaultValues: filterMethods.filterData,
  });

  const { handleSubmit, watch, reset } = formMethods;

  const { totalElements, defaultFilterData, applyFilters, resetFilterData } =
    filterMethods;

  const { upsertCustomer } = useUpsertCustomer();

  useAutoSubmitForm(handleSubmit(applyFilters), watch, ['coachName']);

  useEffect(() => {
    if (!entityId) {
      return;
    }

    // auto submit form on mount and get coaches, useAutoSubmitForm
    const isCoachNotSelected = !selectedCoach; // go from next step to select coach
    const newDefaultFilterData = {
      ...defaultFilterData,
      consultationType: CONSULTATION_TYPE.OFFLINE,
      entityId,
    };

    if (isCoachNotSelected) {
      resetFilterData(newDefaultFilterData);
      reset(newDefaultFilterData);
    }
  }, [entityId]);

  const handleNextStep = async () => {
    await upsertCustomer();

    stepperMethods.goToNextStep();
  };

  const handlePreviousStep = () => {
    coachStore.resetSelectCoachList();
    stepperMethods.goToPreviousStep();
  };

  return (
    <FormProvider {...formMethods}>
      <PageTitle
        className={classes.title}
        title={`Coach list (${totalElements || 0})`}
        customTopMargin
      />
      <CoachTable />
      <ButtonRow
        yesHandler={handleNextStep}
        yesDisabled={!selectedCoach}
        noHandler={handlePreviousStep}
      />
    </FormProvider>
  );
});

export { SelectCoachStep };
